import React, { useState, useEffect } from "react";
import {
  Guild,
  MusicPlayerState,
  VoiceChannel,
  apiService,
} from "../services/api";

interface MusicPlayerProps {
  guilds: Guild[];
  selectedGuild: string;
  setSelectedGuild: (guildId: string) => void;
  musicStatus: MusicPlayerState | null;
  onRefreshMusicStatus: () => void;
}

export const MusicPlayer: React.FC<MusicPlayerProps> = ({
  guilds,
  selectedGuild,
  setSelectedGuild,
  musicStatus,
  onRefreshMusicStatus,
}) => {
  const [musicQuery, setMusicQuery] = useState("");
  const [showFullQueue, setShowFullQueue] = useState(false);
  const [voiceChannels, setVoiceChannels] = useState<VoiceChannel[]>([]);
  const [textChannels, setTextChannels] = useState<{ id: string; name: string }[]>([]);
  const [selectedVoiceChannel, setSelectedVoiceChannel] = useState<string>("");
  const [selectedTextChannel, setSelectedTextChannel] = useState<string>("");
  const [playingLoading, setPlayingLoading] = useState(false);

  // Auto-refresh music status every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      onRefreshMusicStatus();
    }, 3000);
    return () => clearInterval(interval);
  }, [onRefreshMusicStatus]);

  // Load voice & text channels when guild changes
  useEffect(() => {
    const loadChannels = async () => {
      if (!selectedGuild) return;

      try {
        const [vChannels, gChannels] = await Promise.all([
          apiService.getVoiceChannels(selectedGuild),
          apiService.getGuildChannels(selectedGuild),
        ]);

        setVoiceChannels(vChannels);
        if (vChannels.length > 0 && !selectedVoiceChannel) {
          setSelectedVoiceChannel(vChannels[0].id);
        }

        const tChannels = gChannels.channels.filter((c) => c.type === "text");
        setTextChannels(tChannels);
        if (tChannels.length > 0 && !selectedTextChannel) {
          setSelectedTextChannel(tChannels[0].id);
        }
      } catch (error) {
        console.error("Failed to load guild channels for music:", error);
      }
    };

    loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGuild]);

  const handlePlayMusic = async () => {
    if (!musicQuery.trim() || !selectedGuild) return;

    try {
      setPlayingLoading(true);
      const voiceId = selectedVoiceChannel || (voiceChannels[0] ? voiceChannels[0].id : "");
      const textId = selectedTextChannel || (textChannels[0] ? textChannels[0].id : "");

      await apiService.playMusic(musicQuery.trim(), selectedGuild, voiceId, textId);
      setMusicQuery("");
      onRefreshMusicStatus();
    } catch (error: any) {
      alert("Errore riproduzione: " + (error.response?.data?.error || error.message));
    } finally {
      setPlayingLoading(false);
    }
  };

  const handlePauseResume = async () => {
    if (!selectedGuild) return;
    try {
      if (musicStatus?.isPlaying && !musicStatus?.paused) {
        await apiService.pauseMusic(selectedGuild);
      } else {
        await apiService.resumeMusic(selectedGuild);
      }
      onRefreshMusicStatus();
    } catch (error) {
      console.error("Pause/resume error:", error);
    }
  };

  const handleSkipTrack = async () => {
    if (!selectedGuild) return;
    try {
      await apiService.skipTrack(selectedGuild);
      onRefreshMusicStatus();
    } catch (error) {
      console.error("Failed to skip track:", error);
    }
  };

  const handleStopMusic = async () => {
    if (!selectedGuild) return;
    try {
      await apiService.stopMusic(selectedGuild);
      onRefreshMusicStatus();
    } catch (error) {
      console.error("Failed to stop music:", error);
    }
  };

  const handleSkipToTrack = async (trackIndex: number) => {
    if (!selectedGuild) return;
    try {
      await apiService.skipToTrack(selectedGuild, trackIndex);
      onRefreshMusicStatus();
    } catch (error) {
      console.error("Failed to skip to track:", error);
    }
  };

  const handleVolumeChange = async (volume: number) => {
    if (!selectedGuild) return;
    try {
      await apiService.setVolume(selectedGuild, volume);
      onRefreshMusicStatus();
    } catch (error) {
      console.error("Failed to set volume:", error);
    }
  };

  const formatDuration = (ms: number | undefined) => {
    if (!ms) return "0:00";
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="tab-content music-player-page">
      <div className="card-header-row">
        <div>
          <h2>Player Musicale Lavalink</h2>
          <p className="subtitle">Controlla la riproduzione in streaming nei canali vocali Discord</p>
        </div>

        <div className="guild-picker">
          <label>Server:</label>
          <select
            value={selectedGuild}
            onChange={(e) => setSelectedGuild(e.target.value)}
            className="select-input"
          >
            {guilds.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedGuild && (
        <>
          {/* Audio Channels Bar */}
          <div className="card channel-select-bar">
            <div className="grid-2-col">
              <div className="form-group">
                <label>🔊 Canale Vocale</label>
                <select
                  value={selectedVoiceChannel}
                  onChange={(e) => setSelectedVoiceChannel(e.target.value)}
                  className="select-input"
                >
                  <option value="">Seleziona Canale Vocale...</option>
                  {voiceChannels.map((ch) => (
                    <option key={ch.id} value={ch.id}>
                      {ch.name} ({ch.memberCount} connessi)
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>💬 Canale Testuale Notifiche</label>
                <select
                  value={selectedTextChannel}
                  onChange={(e) => setSelectedTextChannel(e.target.value)}
                  className="select-input"
                >
                  <option value="">Seleziona Canale Testuale...</option>
                  {textChannels.map((ch) => (
                    <option key={ch.id} value={ch.id}>
                      #{ch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Search & Play */}
          <div className="card music-search-card">
            <div className="search-row">
              <input
                type="text"
                value={musicQuery}
                onChange={(e) => setMusicQuery(e.target.value)}
                placeholder="🔍 Cerca brano o incolla link YouTube / Spotify / SoundCloud..."
                onKeyDown={(e) => e.key === "Enter" && handlePlayMusic()}
                className="text-input flex-1"
              />
              <button
                onClick={handlePlayMusic}
                disabled={!musicQuery.trim() || playingLoading}
                className="btn primary-btn play-btn"
              >
                {playingLoading ? "Caricamento..." : "▶️ Riproduci / Aggiungi"}
              </button>
            </div>
          </div>

          {/* Now Playing Console */}
          <div className="card now-playing-card">
            <div className="card-header-row">
              <h3>🎵 Brano in Riproduzione</h3>
              <span className={`status-pill ${musicStatus?.isPlaying ? "online" : "offline"}`}>
                {musicStatus?.isPlaying ? (musicStatus.paused ? "IN PAUSA" : "IN RIPRODUZIONE") : "INATTIVO"}
              </span>
            </div>

            {musicStatus?.currentTrack ? (
              <div className="now-playing-content">
                <img
                  src={musicStatus.currentTrack.thumbnail || "https://cdn.discordapp.com/embed/avatars/0.png"}
                  alt={musicStatus.currentTrack.title}
                  className="track-art"
                />
                <div className="track-info-pane">
                  <h3 className="track-title">{musicStatus.currentTrack.title}</h3>
                  <p className="track-author">{musicStatus.currentTrack.author}</p>
                  {musicStatus.currentTrack.requester && (
                    <small className="track-requester">Richiesto da: {musicStatus.currentTrack.requester}</small>
                  )}

                  {/* Controls */}
                  <div className="player-controls-row">
                    <button onClick={handlePauseResume} className="btn control-btn">
                      {musicStatus?.paused ? "▶️ Riprendi" : "⏸️ Pausa"}
                    </button>
                    <button onClick={handleSkipTrack} className="btn control-btn">
                      ⏭️ Salta (Skip)
                    </button>
                    <button onClick={handleStopMusic} className="btn control-btn danger">
                      ⏹️ Ferma & Esci
                    </button>
                  </div>

                  {/* Volume Slider */}
                  <div className="volume-slider-row">
                    <span>🔈</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={musicStatus?.volume || 50}
                      onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                      className="volume-range"
                    />
                    <span className="volume-val">{musicStatus?.volume || 50}%</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>Nessun brano in riproduzione al momento. Usa la barra in alto per cercare un brano.</p>
              </div>
            )}
          </div>

          {/* Queue List */}
          {musicStatus?.queue && musicStatus.queue.length > 0 && (
            <div className="card">
              <div className="card-header-row">
                <h3>Coda di Riproduzione ({musicStatus.queue.length} brani)</h3>
                {musicStatus.queue.length > 8 && (
                  <button
                    className="btn secondary-btn small-btn"
                    onClick={() => setShowFullQueue(!showFullQueue)}
                  >
                    {showFullQueue ? "Mostra meno" : `Mostra tutti (${musicStatus.queue.length})`}
                  </button>
                )}
              </div>

              <div className="queue-items-list">
                {(showFullQueue ? musicStatus.queue : musicStatus.queue.slice(0, 8)).map(
                  (track, idx) => (
                    <div
                      key={idx}
                      className="queue-item-row"
                      onClick={() => handleSkipToTrack(idx + 1)}
                      title="Clicca per saltare a questo brano"
                    >
                      <span className="queue-idx">#{idx + 1}</span>
                      <img
                        src={track.thumbnail || "https://cdn.discordapp.com/embed/avatars/0.png"}
                        alt={track.title}
                        className="queue-thumb"
                      />
                      <div className="queue-track-details">
                        <h5>{track.title}</h5>
                        <p>{track.author}</p>
                      </div>
                      <span className="queue-duration">{formatDuration(track.duration)}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
